import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Nav, Row, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TABLE_CONTEXT } from 'src/services/posthogEvents';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import ColumnConfiguration from 'src/components/custom_components/ColumnConfiguration';
import CustomTable, {
  Column,
} from 'src/components/custom_components/CustomTable/CustomTable';
import OrderStatusBadge from 'src/components/custom_components/OrderStatusBadge';
import SitesDropdown from 'src/components/custom_components/SitesDropdown';
import GradingReceivedNumberBadge from 'src/components/GradingReceivedNumberBadge';
import OrderDetailsButton from 'src/components/OrderDetailsButton';
import OrderDetailsDrawer from 'src/components/OrderDetailsDrawer';
import ReportViewer from 'src/components/ReportViewer/ReportViewer';
import SearchPatientInfoFormGroup, {
  SearchPatientFormValues,
} from 'src/components/SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';

import { Api } from 'src/models/Api.model';
import { DEFAULT_PAGE_SIZE } from 'src/models/DefaultPageSize.model';
import { CriteriaGroupGroupElementsInnerOperatorEnum } from 'src/models/GalenData/CriteriaGroupGroupElementsInner.model';
import { RetinalExamKeyFiledEnum } from 'src/models/GalenData/CustomDeviceDataModel.model';
import { DeviceDataView } from 'src/models/GalenData/DeviceDataView.model';
import { OrderData, OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { I18NextNS } from 'src/models/Language.model';
import { transformExamToSession } from 'src/models/Session.model';

import useColumnVisibility from 'src/hooks/useColumnVisibility';
import useDateFormats from 'src/hooks/useDateFormats';
import { useGetFilterOrder } from 'src/hooks/useGetFilterOrder';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import { usePollFilteredOrders } from 'src/hooks/usePollFilteredOrders';

import { captureUserActionError } from 'src/utils/errorReporting';

enum OrderResultsTab {
  NEW_RESULTS = 'NEW_RESULTS',
  PENDING_RESULTS = 'PENDING_RESULTS',
  ORDER_CLOSED = 'CLOSED_ORDERS',
}

const TAB_TO_STATUS_MAP: Record<OrderResultsTab, OrderStatusEnum[]> = {
  [OrderResultsTab.NEW_RESULTS]: [OrderStatusEnum.REVIEW],
  [OrderResultsTab.PENDING_RESULTS]: [OrderStatusEnum.PENDING],
  [OrderResultsTab.ORDER_CLOSED]: [
    OrderStatusEnum.COMPLETE,
    OrderStatusEnum.CLOSED,
    OrderStatusEnum.CANCELLED,
  ],
};

export default function TeleophthStandaloneResults() {
  const { t: tPages } = useTranslation(I18NextNS.PAGES);
  const { t } = useTranslation(I18NextNS.COMMON);
  const { formatDate, formatDatePart, formatTimePart } = useDateFormats();
  const [activeTab, setActiveTab] = useState<OrderResultsTab>(
    OrderResultsTab.NEW_RESULTS,
  );
  const [currentOrdersPage, setCurrentOrdersPage] = useState(1);
  const [showViewer, setShowViewer] = useState<boolean>(false);
  const [searchValues, setSearchValues] = useState<SearchPatientFormValues>();
  const [isOrderDetailsDrawerOpen, setIsOrderDetailsDrawerOpen] =
    useState(false);
  const [selectedOrder, setSelectedOrder] =
    useState<DeviceDataView<OrderData>>();

  const {
    getFilteredOrders,
    getFilteredOrdersCore,
    ordersData,
    isGettingOrders,
  } = useGetFilterOrder();
  const { showHttpRequestError } = useHandleHttpRequestError();

  const { selectedSiteId, gradingReceivedCountResetToken } = useLocalStore();

  const {
    userSettings: { userPermission },
    setSession,
  } = useRemoteBoundStore();

  const { teleophthOrderResultsColsKeys, setTeleophthOrderResultsColsKeys } =
    useLocalStore();

  const onViewReport = useCallback(
    async (orderData: DeviceDataView<OrderData>) => {
      try {
        if (!userPermission.read) {
          throw new Error(
            t('message.error.noPermissionViewReport', { ns: I18NextNS.COMMON }),
          );
        }

        if (!orderData.owner?.userId) {
          throw new Error(
            t('message.error.userNotFoundInOrder', { ns: I18NextNS.COMMON }),
          );
        }

        const examResult = await Api.remote.get_exams({
          ownerFilter: {
            users: [orderData.owner?.userId],
          },
          groupElements: [
            {
              key: RetinalExamKeyFiledEnum.OrderId,
              value: orderData.data?.OrderId.value,
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            },
          ],
        });

        const exam = examResult.data.content?.[0];
        if (!exam) {
          throw new Error(
            t('message.error.orderNotFound', { ns: I18NextNS.COMMON }),
          );
        }

        const freshOrderResult = await Api.remote.get_exam_order({
          groupElements: [
            {
              key: 'OrderId',
              value: orderData.data?.OrderId.value,
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            },
          ],
        });

        const freshOrder = freshOrderResult.data.content?.[0];
        if (!freshOrder) {
          throw new Error(
            t('message.error.orderNotFound', { ns: I18NextNS.COMMON }),
          );
        }

        const session = transformExamToSession(exam);
        session.order = freshOrder.data;

        setSession(session);

        setShowViewer(true);
      } catch (error) {
        const errorMessage = showHttpRequestError(error, {
          messagePrefix: t('message.error.failedToViewReport', {
            ns: I18NextNS.COMMON,
          }),
        });

        captureUserActionError(error, 'view_teleophth_report', {
          extras: {
            errorMessage,
          },
        });
      }
    },
    [userPermission.read, t, showHttpRequestError, setSession],
  );

  const currentSelectedStatus = useMemo(
    () => TAB_TO_STATUS_MAP[activeTab],
    [activeTab],
  );

  const dateColumnTitle = useMemo(() => {
    if (activeTab === OrderResultsTab.NEW_RESULTS) {
      return t('orderFields.dateResultReceived');
    }
    if (activeTab === OrderResultsTab.PENDING_RESULTS) {
      return t('orderFields.dateExamSubmitted');
    }
    return t('orderFields.dateOrderCompleted');
  }, [activeTab, t]);

  const columns: Column<DeviceDataView<OrderData>>[] = useMemo(
    () => [
      ...(activeTab === OrderResultsTab.NEW_RESULTS
        ? [
            {
              title: t('orderFields.priorityStatus'),
              dataIndex: 'priorityStatus',
              dataTest: 'result-priority-status-cell',
              render: (data: DeviceDataView<OrderData>) => {
                return data.data?.Priority?.value === 0 ? (
                  <span
                    className="text-warning fw-bold"
                    data-test="result-priority-status"
                  >
                    {t('orderFields.priorityReview')}
                  </span>
                ) : (
                  <span
                    className="text-gray-500 fw-bold"
                    data-test="result-priority-status"
                  >
                    {t('orderFields.reportAvailable')}
                  </span>
                );
              },
            },
          ]
        : []),
      {
        title: dateColumnTitle,
        dataIndex: 'reportReceivedTime',
        dataTest: 'result-received-date-cell',
        render: (data: DeviceDataView<OrderData>) => {
          const datetime =
            activeTab === OrderResultsTab.NEW_RESULTS
              ? data.data?.ReportDatetime?.value
              : data.maxValueProvidedOn;

          return (
            datetime && (
              <>
                <div>{formatDatePart(datetime)}</div>
                <div>{formatTimePart(datetime)}</div>
              </>
            )
          );
        },
      },
      {
        title: t('patientFields.DOB'),
        dataIndex: t('patientFields.DOB'),
        dataTest: 'result-cell-dob',
        render: (data: DeviceDataView<OrderData>) =>
          formatDate(data.owner?.dateOfBirth),
      },
      {
        title: t('patientFields.First Name'),
        dataIndex: t('patientFields.First Name'),
        dataTest: 'result-cell-first-name',
        render: (data: DeviceDataView<OrderData>) => data.owner?.firstName,
      },
      {
        title: t('patientFields.Last Name'),
        dataIndex: t('patientFields.Last Name'),
        dataTest: 'result-cell-last-name',
        render: (data: DeviceDataView<OrderData>) => data.owner?.lastName,
      },
      {
        title: t('patientFields.PID'),
        dataIndex: t('patientFields.PID'),
        dataTest: 'result-cell-patient-id',
        render: (data: DeviceDataView<OrderData>) => data.owner?.patientId,
      },
      {
        title: t('patientFields.siteName'),
        dataIndex: t('patientFields.siteName'),
        dataTest: 'result-cell-site',
        render: (data: DeviceDataView<OrderData>) => data.data?.SiteName?.value,
      },
      ...(activeTab === OrderResultsTab.ORDER_CLOSED
        ? [
            {
              title: t('orderFields.orderStatus'),
              dataIndex: t('orderFields.orderStatus'),
              render: (data: DeviceDataView<OrderData>) =>
                OrderStatusBadge(data.data?.Status.value),
            },
          ]
        : []),
      ...(activeTab === OrderResultsTab.NEW_RESULTS
        ? [
            {
              title: t('orderFields.actions'),
              dataIndex: t('orderFields.actions'),
              render: (data: DeviceDataView<OrderData>) => (
                <Button
                  variant="outline-primary"
                  onClick={() => onViewReport(data)}
                  className="rounded-3 px-3 py-1"
                  data-test="result-view-report-button"
                  data-track="results.teleophth-standalone.view-report"
                  data-observe
                >
                  <FontAwesomeIcon icon={faEye} />
                </Button>
              ),
            },
          ]
        : []),
      ...(activeTab === OrderResultsTab.PENDING_RESULTS ||
      activeTab === OrderResultsTab.ORDER_CLOSED
        ? [
            {
              title: t('orderFields.actions'),
              dataIndex: t('orderFields.actions'),
              render: (data: DeviceDataView<OrderData>) => (
                <OrderDetailsButton
                  onClick={() => {
                    setSelectedOrder(data);
                    setIsOrderDetailsDrawerOpen(true);
                  }}
                />
              ),
            },
          ]
        : []),
    ],
    [
      activeTab,
      t,
      dateColumnTitle,
      onViewReport,
      formatDate,
      formatDatePart,
      formatTimePart,
    ],
  );

  const { visibleColumns, handleToggleColumn, displayedColumns } =
    useColumnVisibility(
      columns,
      teleophthOrderResultsColsKeys,
      setTeleophthOrderResultsColsKeys,
      { tableContext: TABLE_CONTEXT.STANDALONE_RESULTS },
    );

  const getTabClassName = (tab: OrderResultsTab) => {
    const isActive = activeTab === tab;
    return isActive ? 'active border-primary border-bottom-0' : 'text-primary';
  };

  const isNewResultsTabActive = useMemo(() => {
    return activeTab === OrderResultsTab.NEW_RESULTS;
  }, [activeTab]);

  const isSearchValuesEmpty = useMemo(() => {
    return (
      searchValues === undefined ||
      Object.values(searchValues).every((value) => !value)
    );
  }, [searchValues]);

  const shouldPollNewResults = useMemo(() => {
    return isNewResultsTabActive && isSearchValuesEmpty;
  }, [isNewResultsTabActive, isSearchValuesEmpty]);

  const { resetGradingReceivedCountPolling } = useLocalStore();

  useEffect(() => {
    if (shouldPollNewResults) {
      resetGradingReceivedCountPolling();
    } else {
      getFilteredOrders({
        status: currentSelectedStatus,
        siteId: selectedSiteId,
        pageNumber: 0,
        searchValues,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isPolling } = usePollFilteredOrders({
    shouldPoll: shouldPollNewResults,
    getFilteredOrdersCore: getFilteredOrdersCore,
    currentPage: currentOrdersPage,
    setCurrentPage: setCurrentOrdersPage,
    selectedSiteId,
    searchValues,
    currentSelectedStatus,
    resetToken: gradingReceivedCountResetToken,
  });

  const handleActiveTabChange = useCallback(
    (tab: OrderResultsTab) => {
      setActiveTab(tab);

      if (tab === OrderResultsTab.NEW_RESULTS && isSearchValuesEmpty) {
        resetGradingReceivedCountPolling();
      } else {
        const statuses = TAB_TO_STATUS_MAP[tab];
        getFilteredOrders({
          status: statuses,
          siteId: selectedSiteId,
          pageNumber: 0,
          searchValues,
        });
      }
      setCurrentOrdersPage(1);
    },
    [
      getFilteredOrders,
      isSearchValuesEmpty,
      resetGradingReceivedCountPolling,
      searchValues,
      selectedSiteId,
      setCurrentOrdersPage,
    ],
  );

  const resetNonSiteFilters = useCallback(() => {
    setSearchValues(undefined);

    if (isNewResultsTabActive) {
      resetGradingReceivedCountPolling();
    } else {
      getFilteredOrders({
        status: currentSelectedStatus,
        siteId: selectedSiteId,
        pageNumber: 0,
        searchValues: undefined,
      });
    }
    setCurrentOrdersPage(1);
  }, [
    currentSelectedStatus,
    getFilteredOrders,
    isNewResultsTabActive,
    resetGradingReceivedCountPolling,
    selectedSiteId,
  ]);

  const handleCloseReportViewer = useCallback(() => {
    resetNonSiteFilters();
    setShowViewer(false);
  }, [resetNonSiteFilters]);

  return (
    <Stack gap={3} className="h-100">
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-center"
        data-test="exam-results-page"
      >
        <h3 className="mb-0">{tPages('results.title')}</h3>
      </Stack>
      <Stack direction="horizontal" gap={3} className="align-items-start">
        <div className="h-100" style={{ paddingTop: '2px' }}>
          <Form.Label>{t('sites.title')}</Form.Label>
          <SitesDropdown
            toggleClassName="py-2"
            handleSiteChange={(siteId) => {
              if (shouldPollNewResults) {
                resetGradingReceivedCountPolling();
              } else {
                getFilteredOrders({
                  status: currentSelectedStatus,
                  siteId: siteId,
                  pageNumber: 0,
                  searchValues,
                });
              }
              setCurrentOrdersPage(1);
            }}
          />
        </div>
        <SearchPatientInfoFormGroup
          values={searchValues}
          onSearchValuesChange={(searchValues) => {
            setSearchValues(searchValues);

            if (
              isNewResultsTabActive &&
              Object.values(searchValues).every((value) => !value)
            ) {
              resetGradingReceivedCountPolling();
            } else {
              getFilteredOrders({
                status: currentSelectedStatus,
                siteId: selectedSiteId,
                pageNumber: 0,
                searchValues,
              });
            }
            setCurrentOrdersPage(1);
          }}
          onFormReset={resetNonSiteFilters}
        />
        <div style={{ marginTop: '30px' }}>
          <ColumnConfiguration
            columns={columns}
            visibleColumns={visibleColumns}
            onToggleColumn={handleToggleColumn}
          />
        </div>
      </Stack>
      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        <Stack className="h-100">
          <Row className="align-items-center">
            <Col>
              <Nav variant="tabs" className="fw-bold">
                <Nav.Item>
                  <Nav.Link
                    data-test="new-results-tab"
                    className={getTabClassName(OrderResultsTab.NEW_RESULTS)}
                    onClick={() =>
                      handleActiveTabChange(OrderResultsTab.NEW_RESULTS)
                    }
                  >
                    <div className="d-flex gap-1">
                      {t('orderFields.newResults')}
                      <GradingReceivedNumberBadge />
                    </div>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    data-test="pending-results-tab"
                    className={getTabClassName(OrderResultsTab.PENDING_RESULTS)}
                    onClick={() =>
                      handleActiveTabChange(OrderResultsTab.PENDING_RESULTS)
                    }
                  >
                    {t('orderFields.pendingResults')}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    data-test="closed-orders-tab"
                    className={getTabClassName(OrderResultsTab.ORDER_CLOSED)}
                    onClick={() =>
                      handleActiveTabChange(OrderResultsTab.ORDER_CLOSED)
                    }
                  >
                    {t('orderFields.closedOrders')}
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
          </Row>

          <div className="flex-grow-1" style={{ minHeight: 0 }}>
            <CustomTable
              isLoading={isGettingOrders || isPolling}
              dataSource={ordersData?.content ?? []}
              rowKey="deviceDataId"
              tableContext={TABLE_CONTEXT.STANDALONE_RESULTS}
              rowProps={(record) =>
                ({
                  'data-test': `result-row-${record.owner?.patientId ?? record.deviceDataId}`,
                }) as React.HTMLAttributes<HTMLTableRowElement>
              }
              pagination={{
                defaultPageSize: DEFAULT_PAGE_SIZE,
                pageSizeOptions: [DEFAULT_PAGE_SIZE],
                totalPages: ordersData?.totalPages ?? 0,
                currentPage: currentOrdersPage,
                handleSetCurrentPage: setCurrentOrdersPage,
                onChangePage: (page) => {
                  getFilteredOrders({
                    siteId: selectedSiteId,
                    pageNumber: page - 1,
                    searchValues,
                    status: currentSelectedStatus,
                  });
                },
              }}
              columns={displayedColumns}
              empty={{ buttons: [] }}
            />
          </div>
        </Stack>
        {showViewer && (
          <ReportViewer
            show={showViewer}
            handleCloseReportViewer={handleCloseReportViewer}
            handleRefreshOrders={resetNonSiteFilters}
          />
        )}
        <OrderDetailsDrawer
          show={isOrderDetailsDrawerOpen}
          onHide={() => setIsOrderDetailsDrawerOpen(false)}
          order={selectedOrder}
          showOrderStatus={true}
        />
      </div>
    </Stack>
  );
}
